import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  Typography,
  Checkbox,
  Button,
  InputBase,
  MenuItem,
  Select,
  CircularProgress,
  Chip,
  TextField,
  Stack,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import { useTheme } from "@mui/material/styles";
import ApiConfig from "../../../Config";
import productService from "../../../repository/productRepository";

const AddToSponsored = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const colors = {
    bg: isDark ? "#11100F" : "#F6F3EF",
    card: isDark ? "#191715" : "#FFFFFF",
    input: isDark ? "#151412" : "#FCFBF9",
    text: isDark ? "#F4F0EA" : "#27231F",
    secondary: isDark ? "#AAA49C" : "#77716A",
    accent: isDark ? "#D1B99A" : "#806B52",
    accentSoft: isDark ? "rgba(209,185,154,.12)" : "rgba(128,107,82,.08)",
    border: isDark ? "rgba(255,255,255,.08)" : "rgba(50,40,30,.09)",
    success: isDark ? "#8FC6A3" : "#3D8054",
    danger: isDark ? "#D99494" : "#B54545",
  };

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortField, setSortField] = useState("productName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [productDates, setProductDates] = useState({});
  const [priority, setPriority] = useState(1);
  const [placement, setPlacement] = useState("homepage");
  const [sponsoredProducts, setSponsoredProducts] = useState([]);
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const response = await productService.getAllProducts();
      const productsData = (response.data || []).map((item) => ({
        id: item._id,
        productId: item.product?.productId,
        productName: item.product?.title || "Untitled Product",
        product_ShortDescription: item.product?.description || "",
        s_Price: item.product?.rentAmount,
        size: item.product?.size || "-",
        productStatus: Boolean(item.productStatus),
        productImage: item.productImage || [],
      }));
      setProducts(productsData);
    } catch (err) {
      setError("Failed to fetch products");
    }
  };

  const fetchSponsoredProducts = async () => {
    try {
      const response = await productService.getSponsoreProducts();
      const sponsoredData = (response.data.sponsorProduct || []).map(
        (item) => ({
          productId: item.productId,
        }),
      );
      setSponsoredProducts(sponsoredData);
    } catch (err) {
      setError("Failed to fetch sponsored products");
    }
  };

  const fetchData = async (initial = false) => {
    initial ? setLoading(true) : setRefreshing(true);
    await Promise.all([fetchProducts(), fetchSponsoredProducts()]);
    initial ? setLoading(false) : setRefreshing(false);
  };

  useEffect(() => {
    fetchData(true);
  }, []);

  useEffect(() => {
    const sponsoredIds = sponsoredProducts.map((sp) => sp.productId);
    setSelectedProducts((prev) =>
      Array.from(new Set([...prev, ...sponsoredIds])),
    );
  }, [sponsoredProducts]);

  const filteredProducts = useMemo(() => {
    let filtered = products.filter((product) => {
      const query = searchTerm.trim().toLowerCase();
      const matchesSearch =
        !query ||
        [product.productName, product.productId, product.size].some((value) =>
          String(value || "")
            .toLowerCase()
            .includes(query),
        );

      const matchesStatus =
        statusFilter === "all" ||
        product.productStatus === (statusFilter === "active");

      return matchesSearch && matchesStatus;
    });

    filtered.sort((a, b) => {
      const first = String(a[sortField] ?? "").toLowerCase();
      const second = String(b[sortField] ?? "").toLowerCase();
      if (first === second) return 0;
      return sortOrder === "asc"
        ? first > second
          ? 1
          : -1
        : first < second
          ? 1
          : -1;
    });

    return filtered;
  }, [products, searchTerm, statusFilter, sortField, sortOrder]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / rowsPerPage),
  );

  const displayedProducts = filteredProducts.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage,
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const sponsoredCount = sponsoredProducts.length;
  const activeCount = products.filter((p) => p.productStatus).length;
  const selectedCount = selectedProducts.filter(
    (id) => !sponsoredProducts.some((p) => p.productId === id),
  ).length;

  const isProductSponsored = (id) =>
    sponsoredProducts.some((sponsored) => sponsored.productId === id);

  const handleCheckboxChange = (id) => {
    if (isProductSponsored(id)) return;
    setSelectedProducts((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id],
    );
  };

  const handleDateChange = (id, type, value) => {
    setProductDates((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [type]: value,
      },
    }));
  };

  const handleAddToSponsored = async () => {
    try {
      const productsToAdd = selectedProducts
        .map((id) => ({
          productId: id,
          startDate: productDates[id]?.startDate,
          endDate: productDates[id]?.endDate,
        }))
        .filter((product) => product.startDate && product.endDate);

      if (productsToAdd.length === 0) {
        setError("Please select products with start and end dates.");
        return;
      }

      if (productsToAdd.length !== selectedCount) {
        setError("Please add start and end dates for every selected product.");
        return;
      }

      const selectedProductIds = productsToAdd.map(
        (product) => product.productId,
      );

      const body = {
        productId: selectedProductIds,
        startDate: productsToAdd[0]?.startDate,
        endDate: productsToAdd[0]?.endDate,
        isActive: true,
        priority: priority,
        placement: placement,
      };

      setSaving(true);

      await productService.addSponsoreProduct(body);

      setSuccess("Products added to sponsored successfully.");
      setSelectedProducts([]);
      await fetchData();
    } catch (err) {
      setError(
        err?.response?.data?.message || "Failed to add products to sponsored.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSelectAll = () => {
    const availableIds = displayedProducts
      .filter((product) => !isProductSponsored(product.id))
      .map((product) => product.id);

    const allSelected = availableIds.every((id) =>
      selectedProducts.includes(id),
    );

    setSelectedProducts((prev) =>
      allSelected
        ? prev.filter((id) => !availableIds.includes(id))
        : Array.from(new Set([...prev, ...availableIds])),
    );
  };

  const availableDisplayed = displayedProducts.filter(
    (product) => !isProductSponsored(product.id),
  );

  const allDisplayedSelected =
    availableDisplayed.length > 0 &&
    availableDisplayed.every((product) =>
      selectedProducts.includes(product.id),
    );

  const formatPrice = (value) => {
    if (value === undefined || value === null || value === "") return "—";
    return `₹${Number(value).toLocaleString("en-IN")}`;
  };

  const resetFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setSortField("productName");
    setSortOrder("asc");
    setPage(1);
  };

  return (
    <Box
      sx={{
        minHeight: "100%",
        background: colors.bg,
        px: { xs: 2, sm: 3, md: 4 },
        py: { xs: 2, md: 3 },
      }}
    >
      {loading ? (
        <Box
          sx={{
            minHeight: "70vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
          }}
        >
          <CircularProgress size={32} sx={{ color: colors.accent }} />
          <Typography sx={{ fontSize: 12, color: colors.secondary }}>
            Loading product catalogue...
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: { xs: "flex-start", lg: "center" },
              flexDirection: { xs: "column", lg: "row" },
              gap: 2,
              mb: 3,
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: ".13em",
                  textTransform: "uppercase",
                  color: colors.accent,
                  mb: 0.7,
                }}
              >
                Homepage Promotion
              </Typography>

              <Typography
                sx={{
                  fontFamily: "Georgia,serif",
                  fontSize: { xs: 27, sm: 32 },
                  fontWeight: 500,
                  color: colors.text,
                  lineHeight: 1.15,
                }}
              >
                Sponsored Products
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  fontSize: 12,
                  color: colors.secondary,
                }}
              >
                Select products and define their promotional campaign period.
              </Typography>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => fetchData()}
              disabled={refreshing}
              sx={{
                height: 40,
                borderRadius: "9px",
                textTransform: "none",
                borderColor: colors.border,
                color: colors.text,
                fontSize: 12,
                fontWeight: 600,
                "&:hover": {
                  borderColor: colors.accent,
                  background: colors.accentSoft,
                },
              }}
            >
              {refreshing ? "Refreshing..." : "Refresh"}
            </Button>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(3,1fr)" },
              gap: 1.5,
              mb: 2.5,
            }}
          >
            {[
              {
                label: "Total Products",
                value: products.length,
                icon: <Inventory2OutlinedIcon />,
              },
              {
                label: "Active Products",
                value: activeCount,
                icon: <CheckCircleOutlineRoundedIcon />,
              },
              {
                label: "Sponsored",
                value: sponsoredCount,
                icon: <CampaignOutlinedIcon />,
              },
            ].map((stat) => (
              <Paper
                key={stat.label}
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  background: colors.card,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: ".09em",
                        fontWeight: 700,
                        color: colors.secondary,
                      }}
                    >
                      {stat.label}
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.4,
                        fontSize: 24,
                        fontWeight: 700,
                        color: colors.text,
                      }}
                    >
                      {stat.value}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background: colors.accentSoft,
                      color: colors.accent,
                    }}
                  >
                    {React.cloneElement(stat.icon, { sx: { fontSize: 20 } })}
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>

          <Paper
            elevation={0}
            sx={{
              borderRadius: "14px",
              background: colors.card,
              border: `1px solid ${colors.border}`,
              overflow: "hidden",
              boxShadow: "0 8px 30px rgba(40,30,20,.045)",
            }}
          >
            <Box
              sx={{
                p: { xs: 1.5, md: 2 },
                display: "flex",
                flexDirection: { xs: "column", lg: "row" },
                gap: 1.2,
                borderBottom: `1px solid ${colors.border}`,
              }}
            >
              <Box
                sx={{
                  height: 42,
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  px: 1.5,
                  borderRadius: "9px",
                  border: `1px solid ${colors.border}`,
                  background: colors.input,
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: colors.secondary }} />
                <InputBase
                  fullWidth
                  placeholder="Search product name, ID or size"
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                  sx={{
                    ml: 1,
                    fontSize: 12.5,
                    color: colors.text,
                    "& input::placeholder": {
                      color: colors.secondary,
                      opacity: 1,
                    },
                  }}
                />
              </Box>

              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                size="small"
                sx={{
                  minWidth: { xs: "100%", lg: 135 },
                  height: 42,
                  borderRadius: "9px",
                  background: colors.input,
                  color: colors.text,
                  fontSize: 12,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.accent,
                  },
                }}
              >
                <MenuItem value="all">All Products</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>

              <Select
                value={sortField}
                onChange={(e) => setSortField(e.target.value)}
                size="small"
                sx={{
                  minWidth: { xs: "100%", lg: 145 },
                  height: 42,
                  borderRadius: "9px",
                  background: colors.input,
                  color: colors.text,
                  fontSize: 12,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: colors.border,
                  },
                }}
              >
                <MenuItem value="productName">Sort: Name</MenuItem>
                <MenuItem value="s_Price">Sort: Price</MenuItem>
                <MenuItem value="size">Sort: Size</MenuItem>
              </Select>

              <Button
                onClick={() => {
                  setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
                  setPage(1);
                }}
                variant="outlined"
                sx={{
                  minWidth: { xs: "100%", lg: 90 },
                  height: 42,
                  borderRadius: "9px",
                  textTransform: "none",
                  borderColor: colors.border,
                  color: colors.secondary,
                  fontSize: 12,
                  "&:hover": {
                    borderColor: colors.accent,
                    color: colors.accent,
                  },
                }}
              >
                {sortOrder === "asc" ? "A → Z" : "Z → A"}
              </Button>
            </Box>

            <Box
              sx={{
                px: { xs: 1.5, md: 2 },
                py: 1.2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                borderBottom: `1px solid ${colors.border}`,
                flexWrap: "wrap",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                <Checkbox
                  checked={allDisplayedSelected}
                  indeterminate={
                    !allDisplayedSelected &&
                    availableDisplayed.some((product) =>
                      selectedProducts.includes(product.id),
                    )
                  }
                  onChange={handleSelectAll}
                  sx={{
                    p: 0.5,
                    color: colors.secondary,
                    "&.Mui-checked,&.MuiCheckbox-indeterminate": {
                      color: colors.accent,
                    },
                  }}
                />

                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.text,
                    fontWeight: 600,
                  }}
                >
                  Select visible products
                </Typography>

                {selectedCount > 0 && (
                  <Chip
                    icon={
                      <StarRoundedIcon sx={{ fontSize: "15px !important" }} />
                    }
                    label={`${selectedCount} selected`}
                    size="small"
                    sx={{
                      height: 25,
                      background: colors.accentSoft,
                      color: colors.accent,
                      fontSize: 10,
                      fontWeight: 700,
                      "& .MuiChip-icon": {
                        color: colors.accent,
                      },
                    }}
                  />
                )}
              </Box>

              <Typography
                sx={{
                  fontSize: 11,
                  color: colors.secondary,
                }}
              >
                {filteredProducts.length} products found
              </Typography>
            </Box>

            <Box
              sx={{
                overflowX: "auto",
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  background: colors.border,
                  borderRadius: 10,
                },
              }}
            >
              <Box sx={{ minWidth: 1180 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns:
                      "50px 250px 110px 90px 110px 160px 160px 100px 130px",
                    alignItems: "center",
                    px: 2,
                    height: 48,
                    background: colors.input,
                    borderBottom: `1px solid ${colors.border}`,
                  }}
                >
                  {[
                    "",
                    "PRODUCT",
                    "PRICE",
                    "SIZE",
                    "STATUS",
                    "START DATE",
                    "END DATE",
                    "PRIORITY",
                    "PLACEMENT",
                  ].map((header, index) => (
                    <Typography
                      key={index}
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        letterSpacing: ".08em",
                        color: colors.secondary,
                      }}
                    >
                      {header}
                    </Typography>
                  ))}
                </Box>

                {displayedProducts.length === 0 ? (
                  <Box
                    sx={{
                      py: 8,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 52,
                        height: 52,
                        borderRadius: "14px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: colors.accentSoft,
                        color: colors.accent,
                        mb: 1.5,
                      }}
                    >
                      <Inventory2OutlinedIcon />
                    </Box>

                    <Typography
                      sx={{
                        fontFamily: "Georgia,serif",
                        fontSize: 18,
                        color: colors.text,
                      }}
                    >
                      No products found
                    </Typography>

                    <Typography
                      sx={{
                        mt: 0.5,
                        fontSize: 12,
                        color: colors.secondary,
                      }}
                    >
                      Try changing your search or filters.
                    </Typography>

                    <Button
                      onClick={resetFilters}
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        color: colors.accent,
                        fontSize: 12,
                      }}
                    >
                      Clear filters
                    </Button>
                  </Box>
                ) : (
                  displayedProducts.map((product, index) => {
                    const sponsored = isProductSponsored(product.id);
                    const selected = selectedProducts.includes(product.id);

                    return (
                      <Box
                        key={product.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "50px 250px 110px 90px 110px 160px 160px 100px 130px",
                          alignItems: "center",
                          px: 2,
                          minHeight: 82,
                          borderBottom: `1px solid ${colors.border}`,
                          background: selected
                            ? colors.accentSoft
                            : "transparent",
                          transition: "background .18s ease",
                          "&:hover": {
                            background: sponsored
                              ? colors.input
                              : selected
                                ? colors.accentSoft
                                : colors.input,
                          },
                        }}
                      >
                        <Checkbox
                          checked={selected}
                          disabled={sponsored}
                          onChange={() => handleCheckboxChange(product.id)}
                          sx={{
                            p: 0.5,
                            color: colors.secondary,
                            "&.Mui-checked": {
                              color: colors.accent,
                            },
                            "&.Mui-disabled": {
                              color: sponsored
                                ? colors.success
                                : colors.secondary,
                            },
                          }}
                        />

                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.2,
                            minWidth: 0,
                          }}
                        >
                          <Box
                            sx={{
                              width: 44,
                              height: 54,
                              borderRadius: "7px",
                              overflow: "hidden",
                              flexShrink: 0,
                              background: colors.input,
                              border: `1px solid ${colors.border}`,
                            }}
                          >
                            {product.productImage?.[0] ? (
                              <Box
                                component="img"
                                src={product.productImage[0]}
                                alt={product.productName}
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            ) : (
                              <Box
                                sx={{
                                  width: "100%",
                                  height: "100%",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  color: colors.secondary,
                                }}
                              >
                                <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
                              </Box>
                            )}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              noWrap
                              sx={{
                                fontSize: 12.5,
                                fontWeight: 700,
                                color: colors.text,
                              }}
                            >
                              {product.productName}
                            </Typography>

                            <Typography
                              noWrap
                              sx={{
                                mt: 0.3,
                                fontSize: 9.5,
                                color: colors.secondary,
                                fontFamily: "monospace",
                              }}
                            >
                              {product.productId || product.id}
                            </Typography>

                            {sponsored && (
                              <Chip
                                label="Sponsored"
                                size="small"
                                sx={{
                                  mt: 0.6,
                                  height: 20,
                                  borderRadius: "5px",
                                  background: "rgba(61,128,84,.10)",
                                  color: colors.success,
                                  fontSize: 9,
                                  fontWeight: 700,
                                }}
                              />
                            )}
                          </Box>
                        </Box>

                        <Typography
                          sx={{
                            fontSize: 12,
                            fontWeight: 700,
                            color: colors.text,
                          }}
                        >
                          {formatPrice(product.s_Price)}
                        </Typography>

                        <Typography
                          sx={{
                            fontSize: 12,
                            color: colors.secondary,
                          }}
                        >
                          {product.size || "—"}
                        </Typography>

                        <Chip
                          label={product.productStatus ? "Active" : "Inactive"}
                          size="small"
                          sx={{
                            width: "fit-content",
                            height: 25,
                            borderRadius: "7px",
                            background: product.productStatus
                              ? "rgba(61,128,84,.10)"
                              : "rgba(181,69,69,.08)",
                            color: product.productStatus
                              ? colors.success
                              : colors.danger,
                            fontSize: 10,
                            fontWeight: 700,
                          }}
                        />

                        <TextField
                          type="date"
                          size="small"
                          disabled={sponsored}
                          value={productDates[product.id]?.startDate || ""}
                          onChange={(e) =>
                            handleDateChange(
                              product.id,
                              "startDate",
                              e.target.value,
                            )
                          }
                          sx={{
                            width: 145,
                            "& .MuiOutlinedInput-root": {
                              height: 38,
                              borderRadius: "8px",
                              background: colors.input,
                              fontSize: 11,
                              color: colors.text,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.border,
                            },
                            "& .MuiInputBase-input": {
                              py: 0,
                            },
                          }}
                        />

                        <TextField
                          type="date"
                          size="small"
                          disabled={sponsored}
                          value={productDates[product.id]?.endDate || ""}
                          onChange={(e) =>
                            handleDateChange(
                              product.id,
                              "endDate",
                              e.target.value,
                            )
                          }
                          sx={{
                            width: 145,
                            "& .MuiOutlinedInput-root": {
                              height: 38,
                              borderRadius: "8px",
                              background: colors.input,
                              fontSize: 11,
                              color: colors.text,
                            },
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: colors.border,
                            },
                            "& .MuiInputBase-input": {
                              py: 0,
                            },
                          }}
                        />

                        <TextField
                          size="small"
                          value={priority}
                          disabled
                          sx={{
                            width: 75,
                            "& .MuiOutlinedInput-root": {
                              height: 38,
                              borderRadius: "8px",
                              background: colors.input,
                              fontSize: 11,
                            },
                          }}
                        />

                        <TextField
                          size="small"
                          value={placement}
                          disabled
                          sx={{
                            width: 105,
                            "& .MuiOutlinedInput-root": {
                              height: 38,
                              borderRadius: "8px",
                              background: colors.input,
                              fontSize: 11,
                            },
                          }}
                        />
                      </Box>
                    );
                  })
                )}
              </Box>
            </Box>

            <Box
              sx={{
                px: 2,
                py: 1.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                flexWrap: "wrap",
                borderTop: `1px solid ${colors.border}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(1);
                  }}
                  size="small"
                  sx={{
                    height: 34,
                    minWidth: 70,
                    borderRadius: "7px",
                    fontSize: 11,
                    color: colors.text,
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: colors.border,
                    },
                  }}
                >
                  <MenuItem value={5}>5</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>

                <Typography
                  sx={{
                    fontSize: 11,
                    color: colors.secondary,
                  }}
                >
                  {filteredProducts.length === 0
                    ? "0 products"
                    : `${(page - 1) * rowsPerPage + 1}–${Math.min(
                        page * rowsPerPage,
                        filteredProducts.length,
                      )} of ${filteredProducts.length}`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={page <= 1}
                  onClick={() => setPage((prev) => prev - 1)}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: "7px",
                    color: colors.secondary,
                  }}
                >
                  <ChevronLeftRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>

                <Typography
                  sx={{
                    minWidth: 70,
                    textAlign: "center",
                    fontSize: 11,
                    color: colors.secondary,
                  }}
                >
                  Page {page} / {totalPages}
                </Typography>

                <IconButton
                  size="small"
                  disabled={page >= totalPages}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    border: `1px solid ${colors.border}`,
                    borderRadius: "7px",
                    color: colors.secondary,
                  }}
                >
                  <ChevronRightRoundedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={0}
            sx={{
              mt: 2,
              p: { xs: 1.5, md: 2 },
              borderRadius: "12px",
              background: colors.card,
              border: `1px solid ${colors.border}`,
              display: "flex",
              alignItems: { xs: "stretch", md: "center" },
              justifyContent: "space-between",
              gap: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: colors.text,
                }}
              >
                Campaign Settings
              </Typography>

              <Typography
                sx={{
                  mt: 0.35,
                  fontSize: 11,
                  color: colors.secondary,
                }}
              >
                These settings will apply to the sponsored products you add.
              </Typography>
            </Box>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              sx={{ width: { xs: "100%", md: "auto" } }}
            >
              <TextField
                label="Priority"
                size="small"
                value={priority}
                disabled
                sx={{
                  width: { xs: "100%", sm: 100 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: 12,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 11,
                  },
                }}
              />

              <TextField
                label="Placement"
                size="small"
                value={placement}
                disabled
                sx={{
                  width: { xs: "100%", sm: 150 },
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    fontSize: 12,
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: 11,
                  },
                }}
              />
            </Stack>
          </Paper>

          <Box
            sx={{
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1.5,
              flexDirection: { xs: "column", sm: "row" },
            }}
          >
            {selectedCount > 0 && (
              <Typography
                sx={{
                  fontSize: 11,
                  color: colors.secondary,
                }}
              >
                {selectedCount} product{selectedCount !== 1 ? "s" : ""} ready to
                sponsor
              </Typography>
            )}

            <Button
              variant="contained"
              startIcon={<SaveRoundedIcon />}
              onClick={handleAddToSponsored}
              disabled={selectedCount === 0 || saving}
              sx={{
                width: { xs: "100%", sm: "auto" },
                minWidth: 190,
                height: 44,
                borderRadius: "10px",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 700,
                background: colors.accent,
                color: "#fff",
                boxShadow: "none",
                "&:hover": {
                  background: isDark ? "#BCA485" : "#6F5B45",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  background: isDark ? "rgba(255,255,255,.08)" : "#E4E0DB",
                  color: colors.secondary,
                },
              }}
            >
              {saving ? "Adding..." : "Add to Sponsored"}
            </Button>
          </Box>

          <Snackbar
            open={Boolean(success)}
            autoHideDuration={5000}
            onClose={() => setSuccess("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setSuccess("")}
              severity="success"
              variant="filled"
              sx={{ borderRadius: "10px" }}
            >
              {success}
            </Alert>
          </Snackbar>

          <Snackbar
            open={Boolean(error)}
            autoHideDuration={5000}
            onClose={() => setError("")}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          >
            <Alert
              onClose={() => setError("")}
              severity="error"
              variant="filled"
              sx={{ borderRadius: "10px" }}
            >
              {error}
            </Alert>
          </Snackbar>
        </>
      )}
    </Box>
  );
};

export default AddToSponsored;
